import i18n from 'i18next'
import { ICustomProperty } from 'src/generated-types/customProperty'

export const parseCustomPropertyValues = (
  raw: string | Record<string, unknown> | undefined | null,
): Record<string, unknown> => {
  if (!raw) return {}
  if (typeof raw === 'object') return raw
  try {
    return JSON.parse(raw) ?? {}
  } catch {
    return {}
  }
}

export const parsePropertyLabels = (property: ICustomProperty): { en: string; no: string } => {
  try {
    const parsed = typeof property.label === 'string' ? JSON.parse(property.label) : property.label
    return { en: parsed?.en ?? '', no: parsed?.no ?? '' }
  } catch {
    const label = typeof property.label === 'string' ? property.label : ''
    return { en: label, no: label }
  }
}

export const normalizeCustomPropertyValue = (value: unknown): string | null => {
  if (value == null) return null
  if (Array.isArray(value)) {
    const first = value.find(v => v != null && String(v).trim() !== '')
    return first != null ? String(first) : null
  }
  if (typeof value === 'object') return JSON.stringify(value)
  const text = String(value).trim()
  return text === '' ? null : text
}

export const getCustomPropertyValueWithKey = (
  customValues: Record<string, unknown>,
  property: ICustomProperty,
): { value: string | null; matchedKey?: string } => {
  const labels = parsePropertyLabels(property)
  const candidates = [
    property.id.toString(),
    String(property.id),
    labels.en,
    labels.no,
  ].filter((key): key is string => Boolean(key && key.trim()))

  for (const key of candidates) {
    if (Object.prototype.hasOwnProperty.call(customValues, key)) {
      return { value: normalizeCustomPropertyValue(customValues[key]), matchedKey: key }
    }
  }

  const lowerKeyMap = Object.keys(customValues).reduce<Record<string, string>>((acc, key) => {
    acc[key.toLowerCase()] = key
    return acc
  }, {})

  for (const candidate of candidates) {
    const matchedKey = lowerKeyMap[candidate.toLowerCase()]
    if (matchedKey) {
      return { value: normalizeCustomPropertyValue(customValues[matchedKey]), matchedKey }
    }
  }

  return { value: null }
}

export const getCustomPropertyValue = (
  customValues: Record<string, unknown>,
  property: ICustomProperty,
): string | null => getCustomPropertyValueWithKey(customValues, property).value

export const getCustomPropertyDisplayValue = (
  raw: string | Record<string, unknown> | undefined | null,
  property: ICustomProperty,
  emptyValue = '-',
): string => {
  const customValues = parseCustomPropertyValues(raw)
  return getCustomPropertyValue(customValues, property) ?? emptyValue
}

export const getCustomPropertyValueByKeys = (
  customValues: Record<string, unknown>,
  keys: string[],
): string | null => {
  for (const key of keys) {
    if (Object.prototype.hasOwnProperty.call(customValues, key)) {
      const value = normalizeCustomPropertyValue(customValues[key])
      if (value != null) return value
    }
  }

  const lowerKeyMap = Object.keys(customValues).reduce<Record<string, string>>((acc, key) => {
    acc[key.toLowerCase()] = key
    return acc
  }, {})

  for (const key of keys) {
    const matchedKey = lowerKeyMap[key.toLowerCase()]
    if (matchedKey) {
      const value = normalizeCustomPropertyValue(customValues[matchedKey])
      if (value != null) return value
    }
  }

  return null
}

export const resolveCustomPropertyLabel = (property: ICustomProperty): string => {
  const raw = property.label
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
    return (i18n.language === 'en' ? parsed?.en : parsed?.no) ?? parsed?.no ?? parsed?.en ?? ''
  } catch {
    return typeof raw === 'string' ? raw : ''
  }
}

export const findCustomPropertyByKey = (
  key: string,
  properties: ICustomProperty[] | undefined,
): ICustomProperty | undefined => {
  if (!properties?.length) return undefined

  const normalizedKey = key.trim().toLowerCase()
  return properties.find(property => {
    if (property.id.toString() === key.trim()) return true
    const labels = parsePropertyLabels(property)
    return labels.en.toLowerCase() === normalizedKey || labels.no.toLowerCase() === normalizedKey
  })
}

export const resolveCustomPropertyKeyLabel = (
  key: string,
  properties: ICustomProperty[] | undefined,
): string => {
  const property = findCustomPropertyByKey(key, properties)
  return property ? resolveCustomPropertyLabel(property) : key
}

export const getUnresolvedCustomPropertyIds = (
  customValues: Record<string, unknown>,
  customProperties: ICustomProperty[] | undefined,
): number[] => {
  const consumedKeys = new Set<string>()

  for (const property of customProperties ?? []) {
    const { matchedKey } = getCustomPropertyValueWithKey(customValues, property)
    if (matchedKey) consumedKeys.add(matchedKey)
  }

  const unresolvedIds = new Set<number>()
  for (const key of Object.keys(customValues)) {
    if (consumedKeys.has(key)) continue

    const id = Number(key)
    if (Number.isInteger(id) && id > 0) {
      unresolvedIds.add(id)
    }
  }

  return Array.from(unresolvedIds)
}
